<script setup lang="ts">
import { shallowRef, watch } from "vue"

import { getJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import { searchPath } from "@/routes"

interface SearchResult {
  id: string
  label: string
  suffix?: string
  icon: string
}

const emit = defineEmits<{ select: [id: string] }>()

const query = shallowRef("")
const results = shallowRef<SearchResult[]>([])
const loading = shallowRef(false)

watch(query, async (value, _previous, onCleanup) => {
  const controller = new AbortController()
  onCleanup(() => controller.abort())

  const normalized = value.trim()
  if (!normalized) {
    results.value = []
    loading.value = false
    return
  }

  loading.value = true
  try {
    const response = await getJson(
      `${searchPath()}?q=${encodeURIComponent(normalized)}`,
      controller.signal,
    )
    if (!response.ok) return

    const data = await response.json()
    results.value = [...(data.items ?? []), ...(data.containers ?? [])]
  } catch (error) {
    if (!(error instanceof DOMException && error.name === "AbortError")) throw error
  } finally {
    if (!controller.signal.aborted) loading.value = false
  }
})
</script>

<template>
  <section class="flex flex-col gap-4">
    <UFormField label="Choose an item or container">
      <UInput
        v-model="query"
        :icon="ICON.search"
        placeholder="Search inventory..."
        autocomplete="off"
        class="w-full"
        :loading="loading"
      />
    </UFormField>

    <div v-if="results.length" class="border-default divide-default divide-y rounded-lg border">
      <button
        v-for="result in results"
        :key="result.id"
        type="button"
        class="hover:bg-muted focus-visible:ring-primary flex w-full items-center gap-4 p-4 text-left focus-visible:ring-2 focus-visible:outline-none"
        @click="emit('select', result.id)"
      >
        <UIcon :name="result.icon" class="text-muted size-5 shrink-0" />
        <span class="min-w-0 flex-1">
          <span class="text-highlighted block truncate text-sm font-medium">{{
            result.label
          }}</span>
          <span v-if="result.suffix" class="text-muted block truncate text-xs">
            {{ result.suffix }}
          </span>
        </span>
      </button>
    </div>

    <p v-else-if="query.trim() && !loading" class="text-muted text-sm">
      No matching items or containers.
    </p>
  </section>
</template>
